import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Loader from '../components/Loader'
import { useRegisterMutation } from '../slices/usersApiSlice'
import { setCredentials } from '../slices/authSlice'
import { toast } from "sonner"
import zxcvbn from 'zxcvbn'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { trackEvent } from "../lib/analytics"

const RegisterScreen = () => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwdScore, setPwdScore] = useState(0)
  const [captchaToken, setCaptchaToken] = useState('')

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [register, { isLoading }] = useRegisterMutation()
  const { userInfo } = useSelector((state) => state.auth)
  const { search } = useLocation()

  const sp = new URLSearchParams(search)
  const rawRedirect = sp.get('redirect') || '/'
  const redirect = /^\/(?!\/)/.test(rawRedirect) ? rawRedirect : '/'

  useEffect(() => {
    if (userInfo) {
      navigate(redirect)
    }
  }, [userInfo, redirect, navigate])

  const submitHandler = async (e) => {
    e.preventDefault()

    const trimmedName = name.trim()
    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedName || !trimmedEmail || !password || !confirmPassword) {
      toast.error('All fields are required')
      return
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error('Invalid email address')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    if (zxcvbn(password).score < 3) {
      toast.error('Please choose a stronger password')
      return
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA')
      return
    }

    try {
      const res = await register({
        name: trimmedName,
        email: trimmedEmail,
        password,
        captchaToken,
      }).unwrap()

      if (res?.requiresEmailVerification) {
        trackEvent("sign_up_completed", { verification_required: true });
        toast.success('Account created. Check your email to verify your account.')
        navigate(`/verify-email/notice?email=${encodeURIComponent(trimmedEmail)}`)
        return
      }

      trackEvent("sign_up_completed", { verification_required: false });
      toast.success(`Welcome, ${res?.name || 'your account is ready'}!`)
      dispatch(setCredentials({ ...res }))
      navigate(redirect)
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Registration failed')
    }
  }

  return (
    <section className='mx-auto w-full max-w-md'>
      <div className='rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8'>
        <p className='mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700'>
          Account
        </p>
        <h1 className='mb-2 text-3xl font-semibold text-slate-900'>Sign Up</h1>

        <form onSubmit={submitHandler} className='space-y-4'>
          <div className='grid gap-4 sm:grid-cols-2'>
            <div>
              <label htmlFor='name' className='mb-1.5 block text-sm font-medium text-slate-700'>
                Name
              </label>
              <input
                id='name'
                type='text'
                placeholder='Enter name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className='w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100'
              />
            </div>

            <div>
              <label htmlFor='email' className='mb-1.5 block text-sm font-medium text-slate-700'>
                Email Address
              </label>
              <input
                id='email'
                type='email'
                placeholder='Enter email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className='w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100'
              />
            </div>
          </div>

          <div>
            <label htmlFor='password' className='mb-1.5 block text-sm font-medium text-slate-700'>
              Password
            </label>
            <input
              id='password'
              type='password'
              placeholder='Enter password'
              value={password}
              autoComplete='new-password'
              onChange={(e) => {
                const val = e.target.value
                setPassword(val)
                setPwdScore(zxcvbn(val).score)
              }}
              required
              minLength={8}
              className='w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100'
            />
            <p className='mt-2 text-xs leading-5 text-slate-500'>
              Use at least 8 characters and choose a password with enough variation to pass the
              strength check.
            </p>
            <p
              className={`mt-1 text-xs font-medium ${
                pwdScore < 3 ? 'text-rose-600' : 'text-emerald-600'
              }`}
            >
              {pwdScore < 3 ? 'Weak password' : 'Strong password'}
            </p>
          </div>

          <div>
            <label
              htmlFor='confirmPassword'
              className='mb-1.5 block text-sm font-medium text-slate-700'
            >
              Confirm Password
            </label>
            <input
              id='confirmPassword'
              type='password'
              placeholder='Confirm password'
              value={confirmPassword}
              autoComplete='new-password'
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className='w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100'
            />
          </div>

          <div className='rounded-xl border border-slate-200 bg-white p-3'>
            <HCaptcha
              sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken('')}
            />
          </div>

          <button
            type='submit'
            disabled={isLoading}
            className='inline-flex items-center justify-center rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300'
          >
            Register
          </button>

          {isLoading && <Loader />}
        </form>

        <p className='mt-5 text-sm text-slate-600'>
          Already have an account?{' '}
          <Link
            to={redirect ? `/login?redirect=${redirect}` : '/login'}
            className='font-semibold text-sky-700 no-underline hover:text-sky-800'
          >
            Login
          </Link>
        </p>
        <p className='mt-2 text-xs text-slate-500'>
          By creating an account, you agree to our Privacy Policy.
        </p>
        <p className='mt-2 text-xs leading-5 text-slate-500'>
          Need help with registration or account access? Contact{' '}
          <a
            href='mailto:contact@practidex.com'
            className='font-semibold text-sky-700 no-underline hover:text-sky-800'
          >
            contact@practidex.com
          </a>
          .
        </p>
      </div>
    </section>
  )
}

export default RegisterScreen


